import React, { useState, useEffect } from "react";
import { Accordion } from "react-bootstrap";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import BilingualText from "../../components/BilingualText/BilingualText";
import TransparentLoader from "../../components/Loaders/TransparentLoader";
import { useTable, useGlobalFilter, usePagination } from "react-table";
import { useColumns } from "./columns";
import "../../components/Filters/filtering.css";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown } from "react-bootstrap";
import {
	getDepositorsAction,
	bulkRefreshStatusAction,
	closeDepositorAction,
	refreshDepositorStatusAction,
} from "../../../store/actions/DepositorsActions";
import { getCompaniesForSelectAction } from "../../../store/actions/CompaniesActions";
import DepositsFilterForm from "../../components/Filters/DepositsFilterForm";
import { useTranslation } from 'react-i18next';
import StatisticsCards from "../../components/StatisticsCards";
import {
	getCurrentCompanyId,
	getCurrentBranchId,
} from "../../../util/contextHelper";
import swal from "sweetalert";
import useApiAlert from "../../hooks/useApiAlert";
import DepositorsService from "../../../services/DepositorsService";

const ManageDepositors = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const fromPage = searchParams.get("fromPage");
	const [activeKey, setActiveKey] = useState(null);
	const [showStats, setShowStats] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [currentPageSize, setCurrentPageSize] = useState(10);
	const columns = useColumns(currentPage, currentPageSize);
	const location = useLocation()
	const contextCompanyId = getCurrentCompanyId();
	const contextBranchId = getCurrentBranchId();

	const [statisticsData, setStatisticsData] = useState({
		active_count: 0,
		inactive_count: 0,
		pending_count: 0,
		total_count: 0,
	});
	const [statisticsLoading, setStatisticsLoading] = useState(false);
	const [downloading, setDownloading] = useState(false);

	const handleBack = () => {
		if (fromPage) {
			navigate(fromPage);
		} else {
			navigate(-1);
		}
	};

	const toggleAccordion = () => {
		setActiveKey((prevKey) => (prevKey === "1" ? null : "1"));
	};

	const {
		listdata: data,
		pagination,
		loading,
	} = useSelector((state) => state.depositors);
	const { ApiAlert, showError } = useApiAlert();
	const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } =
		useTable(
			{
				columns,
				data,
				manualPagination: true,
				manualGlobalFilter: true,
				pageCount: pagination?.totalPages || 0,
				initialState: {
					pageIndex: currentPage - 1,
					pageSize: currentPageSize,
				},
			},
			useGlobalFilter,
			usePagination
		);

	useEffect(() => {
		fetchStatistics();
	}, []);

	const handleEdit = (type, typeid) => {
		navigate(`/depositors/${type}/${typeid}`);
	};

	const handleClose = (id, name) => {
		swal({
			title: "Are you sure?",
			text: `This will close depositor: ${name}`,
			icon: "warning",
			buttons: true,
			dangerMode: true,
		}).then((willClose) => {
			if (!willClose) return;

			// call closeDepositorAction with callback so server-side validation can return activeDeposits
			dispatch(
				closeDepositorAction(id, (error, data, status) => {
					if (
						error &&
						error.errors &&
						error.errors.activeDeposits &&
						status === false
					) {
						const active = Array.isArray(error.errors.activeDeposits)
							? error.errors.activeDeposits
							: [];
						const defaultMsg = `Found ${active.length} active deposit${
							active.length > 1 ? "s" : ""
						} linked to ${name}. Please close or transfer these deposits before closing the depositor.`;
						showError(defaultMsg, active, "depositor");
					} else if (status) {
						swal({
							title: "Success!",
							text: "Depositor closed successfully",
							icon: "success",
							button: "OK",
						}).then(() => {
							dispatch(
								getDepositorsAction({
									page: currentPage,
									limit: currentPageSize,
									companyId: contextCompanyId || undefined,
									branchId: contextBranchId || undefined,
								})
							);
							fetchStatistics();
						});
					}
				})
			);
		});
	};

	const handleRefreshStatus = (id, name) => {
		swal({
			title: "Refresh Status",
			text: `Refresh KYC status for depositor: ${name}?`,
			icon: "info",
			buttons: true,
		}).then((willRefresh) => {
			if (willRefresh) {
				dispatch(refreshDepositorStatusAction(id));
			}
		});
	};

	const handleBulkRefresh = () => {
		swal({
			title: "Bulk Refresh All Statuses",
			text: "This will refresh KYC status for all depositors. Continue?",
			icon: "warning",
			buttons: true,
		}).then((willRefresh) => {
			if (willRefresh) {
				dispatch(bulkRefreshStatusAction());
			}
		});
	};

	const fetchStatistics = async () => {
		setStatisticsLoading(true);
		try {
			const response = await DepositorsService.getDepositorsStatistics();
			if (response.success) {
				setStatisticsData(response.data);
			}
		} catch (error) {
			console.error("Failed to fetch depositors statistics:", error);
		} finally {
			setStatisticsLoading(false);
		}
	};

	const statsConfig = [
		{
			title: "Active Depositors",
			value: statisticsData?.active_count || 0,
			format: "number",
			icon: "las la-user-check",
			color: "success",
		},
		{
			title: "Inactive Depositors",
			value: statisticsData?.inactive_count,
			format: "number",
			icon: "las la-user-times",
			color: "danger",
		},
		{
			title: "Pending Depositors",
			value: statisticsData?.pending_count,
			format: "number",
			icon: "las la-user-clock",
			color: "warning",
		},
		{
			title: "Total Depositors",
			value: statisticsData?.total_count,
			format: "number",
			icon: "las la-users",
			color: "primary",
		},
	];

	const depositorFilterFields = [
		{
			name: "search",
			label: "Search",
			type: "text",
			placeholder: "Search by name, phone, email, city, district, or state",
		},
		{
			name: "companyId",
			label: "Company",
			type: "companySearchSelect",
			required: false,
			allowClear: true,
		},
		{
			name: "status",
			label: "Status",
			type: "select",
			options: [
				{ value: "Active", label: "Active" },
				{ value: "Inactive", label: "Inactive" },
				{ value: "Pending", label: "Pending" },
				{ value: "Rejected", label: "Rejected" },
			],
		},
		{ name: "startDate", label: "Start Date", type: "date" },
		{ name: "endDate", label: "End Date", type: "date" },
	];

	const svg1 = (
		<svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
			<g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
				<rect x="0" y="0" width="24" height="24"></rect>
				<circle fill="#000000" cx="5" cy="12" r="2"></circle>
				<circle fill="#000000" cx="12" cy="12" r="2"></circle>
				<circle fill="#000000" cx="19" cy="12" r="2"></circle>
			</g>
		</svg>
	);

	const filterData = (filters) => {
		const contextFilters = {
			companyId: contextCompanyId || undefined,
			branchId: contextBranchId || undefined,
		};

		const mergedFilters = {
			...contextFilters,
			...filters,
		};

		const cleanedObj = Object.fromEntries(
			Object.entries(mergedFilters).filter(
				([_, value]) =>
					value !== null &&
					value !== "" &&
					value !== undefined &&
					(typeof value !== "object" || Object.keys(value).length > 0)
			)
		);

		console.log("cleanedObj", cleanedObj);
		dispatch(
			getDepositorsAction({ page: 1, limit: currentPageSize, ...cleanedObj })
		);
	};

	// Load depositors on component mount and when pagination changes
	useEffect(() => {
		const params = {
			page: currentPage,
			limit: currentPageSize,
			companyId: contextCompanyId || undefined,
			branchId: contextBranchId || undefined,
		};
		dispatch(getDepositorsAction(params));
	}, [
		dispatch,
		currentPage,
		currentPageSize,
		contextCompanyId,
		contextBranchId,
	]);
	const handlePageSizeChange = (newPageSize) => {
		setCurrentPageSize(Number(newPageSize));
		setCurrentPage(1);
	};

	useEffect(() => {
		dispatch(getCompaniesForSelectAction());
	}, [dispatch]);

	return (
		<div className="custom-listing">
			<TransparentLoader show={loading} />
			<PageTitle activeMenu="Depositors" motherMenu="Finance" />
			<ApiAlert />
			<div className="card">
				<div className="card-body px-4 py-3 pb-0">
					<div className="form-head d-flex flex-wrap justify-content-between align-items-center m-0">
						<h2 className="text-black font-w600 mb-0 me-auto pr-3">
							<BilingualText translationKey="depositors.title" />
						</h2>
						{fromPage && (
							<button
								className="btn btn-secondary btn-rounded me-2 d-flex align-items-center text-white"
								onClick={handleBack}
								title="Go back"
							>
								<i className="fas fa-arrow-left me-2"></i>
								<BilingualText translationKey="common.back" />
							</button>
						)}
						<Dropdown className="custom-dropdown me-3">
							<Dropdown.Menu className="dropdown-menu-right">
								<Dropdown.Item onClick={handleBulkRefresh}>
									Bulk Refresh All Statuses
								</Dropdown.Item>
							</Dropdown.Menu>
						</Dropdown>
						<Link
							variant="primary"
							to="/deposits/add"
							className="me-3 btn btn-primary btn-rounded me-3 d-flex align-items-center text-white"
						>
							<i className="las la-plus scale5 me-3"></i>
							<BilingualText translationKey="deposits.addDeposit" />
						</Link>
						<Link
							to="/depositors/create"
							className="btn btn-primary btn-rounded me-3 d-flex align-items-center text-white"
						>
							<i className="las la-plus scale5 me-2"></i>
							<BilingualText translationKey="depositors.addDepositor" />
						</Link>
						<button
							className="btn btn-primary btn-rounded me-2"
							onClick={() => setShowStats(!showStats)}
							title={showStats ? "Hide Statistics" : "Show Statistics"}
						>
							<i
								className={`fa-solid ${showStats ? "fa-eye-slash" : "fa-eye"}`}
							></i>
						</button>
						<button
							className="btn btn-primary btn-rounded"
							onClick={toggleAccordion}
						>
							<i className="fa-solid fa-filter"></i>
						</button>
					</div>
				</div>
				<Accordion activeKey={activeKey} className="accordion">
					<Accordion.Item className="accordion-item" eventKey="1">
						<Accordion.Collapse eventKey="1">
							<div className="accordion-body">
								<div className="basic-form">
									<DepositsFilterForm
										fields={depositorFilterFields}
										onSubmit={(filters) => filterData(filters)}
									/>
								</div>
							</div>
						</Accordion.Collapse>
					</Accordion.Item>
				</Accordion>
			</div>

			{showStats && (
				<StatisticsCards stats={statsConfig} loading={statisticsLoading} />
			)}

			<div className="card table-card">
				<div className="card-body p-0">
					<div className="d-flex justify-content-end align-items-center px-4 py-3 border-bottom">
						<div className="d-flex align-items-center gap-2">
							<Link
								to="/deposits"
								aria-current={
									location.pathname === "/deposits" ? "page" : undefined
								}
								className={
									(location.pathname === "/deposits"
										? "btn btn-primary text-white"
										: "btn btn-outline-primary") +
									" btn-rounded btn-sm d-flex align-items-center text-white-hover"
								}
							>
								<span className="me-2">
									<i className="las la-list" />
								</span>
								<span className="text-end">
									<BilingualText translationKey="deposits.title" />
								</span>
							</Link>
							<Link
								to="/depositors"
								aria-current={
									location.pathname === "/depositors" ? "page" : undefined
								}
								className={
									(location.pathname === "/depositors"
										? "btn btn-primary text-white"
										: "btn btn-outline-primary") +
									" btn-rounded btn-sm d-flex align-items-center text-white-hover"
								}
							>
								<span className="me-2">
									<i className="las la-list" />
								</span>
								<span className="text-end">
									<BilingualText translationKey="depositors.title" />
								</span>
							</Link>
							<Link
								to="/deposits/history"
								aria-current={
									location.pathname.startsWith("/deposits/history")
										? "page"
										: undefined
								}
								className={
									(location.pathname.startsWith("/deposits/history")
										? "btn btn-primary  text-white"
										: "btn btn-outline-primary text-white-hover") +
									" btn-rounded btn-sm d-flex align-items-center"
								}
							>
								<span className="me-2">
									<i className="fas fa-history" />
								</span>
								<span className="text-end">
									<BilingualText translationKey="deposits.historyTitle" />
								</span>
							</Link>
							<Link
								to="/deposits/summary"
								aria-current={
									location.pathname.startsWith("/deposits/summary")
										? "page"
										: undefined
								}
								className={
									(location.pathname.startsWith("/deposits/summary")
										? "btn btn-primary  text-white"
										: "btn btn-outline-primary  text-white-hover") +
									" btn-rounded btn-sm d-flex align-items-center"
								}
							>
								<span className="me-2">
									<i className="fas fa-chart-bar" />
								</span>
								<span className="text-end">
									<BilingualText translationKey="deposits.summaryTitle" />
								</span>
							</Link>
							<button
								className="btn btn-warning btn-rounded btn-sm d-flex align-items-center text-white"
								onClick={async () => {
									setDownloading(true);
									try {
										const params = {};
										// include current filters for company/status/search/date if available
										// contextCompanyId used for scoping
										if (contextCompanyId) params.companyId = contextCompanyId;
										// If this page had filters stored, include them; otherwise leave empty
										const response = await DepositorsService.downloadDepositors(params);
										const contentDisposition = response.headers && (response.headers['content-disposition'] || response.headers['Content-Disposition']) || '';
										let filename = 'depositors.xlsx';
										const match = /filename[^;=\n]*=(?:UTF-8'')?["']?([^;"'\n]*)/.exec(contentDisposition);
										if (match && match[1]) filename = decodeURIComponent(match[1].replace(/['\"]/g, ''));
										const blob = new Blob([response.data], { type: response.data.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
										const url = window.URL.createObjectURL(blob);
										const a = document.createElement('a');
										a.href = url;
										a.download = filename;
										document.body.appendChild(a);
										a.click();
										a.remove();
										window.URL.revokeObjectURL(url);
									} catch (err) {
										console.error('Failed to download depositors', err);
										if (window.swal) window.swal(t('common.somethingWentWrong') || 'Download failed', '', 'error');
									} finally {
										setDownloading(false);
									}
								}}
								disabled={downloading}
								title="Download XLSX"
							>
								{downloading ? <i className="fas fa-spinner fa-spin me-2" /> : <i className="fas fa-download me-2" />}
								<BilingualText translationKey="depositors.download" defaultText="Download" />
							</button>
						</div>
					</div>
					<div className="table-responsive">
						<table {...getTableProps()} className="table table-responsive-md">
							<thead>
								{headerGroups.map((headerGroup) => (
									<tr
										{...headerGroup.getHeaderGroupProps()}
										key={headerGroup.id}
									>
										{headerGroup.headers.map((column) => (
											<th {...column.getHeaderProps()} key={column.id}>
												{column.render("Header")}
											</th>
										))}
									</tr>
								))}
							</thead>
							<tbody {...getTableBodyProps()}>
								{page.length === 0 ? (
									<tr>
										<td colSpan={columns.length} className="text-center">
											No data available
										</td>
									</tr>
								) : (
									page.map((row) => {
										prepareRow(row);
										return (
											<tr {...row.getRowProps()} key={row.id}>
												{row.cells.map((cell) => {
													if (cell.column.id === "actions") {
														return (
															<td {...cell.getCellProps()} key={cell.column.id}>
																<Dropdown>
																	<Dropdown.Toggle
																		variant="success"
																		className="light sharp i-false btn-xs"
																	>
																		{svg1}
																	</Dropdown.Toggle>
																	<Dropdown.Menu>
																		<Dropdown.Item
																			onClick={() =>
																				handleEdit("edit", cell.row.original.id)
																			}
																		>
																			Edit
																		</Dropdown.Item>
																		<Dropdown.Item
																			onClick={() =>
																				handleEdit("view", cell.row.original.id)
																			}
																		>
																			View Details
																		</Dropdown.Item>
																		<Dropdown.Item
																			onClick={() =>
																				handleEdit("kyc", cell.row.original.id)
																			}
																		>
																			Manage Documents
																		</Dropdown.Item>

																		
																	</Dropdown.Menu>
																</Dropdown>
															</td>
														);
													}
													return (
														<td {...cell.getCellProps()} key={cell.column.id}>
															{cell.render("Cell")}
														</td>
													);
												})}
											</tr>
										);
									})
								)}
							</tbody>
						</table>

						<div className="d-flex justify-content-between align-items-center mt-3">
							<div className="dataTables_info">
								Showing{" "}
								<strong>
									{pagination?.totalRecords > 0
										? (currentPage - 1) * currentPageSize + 1
										: 0}
								</strong>{" "}
								to{" "}
								<strong>
									{Math.min(
										currentPage * currentPageSize,
										pagination?.totalRecords || 0
									)}
								</strong>{" "}
								of <strong>{pagination?.totalRecords || 0}</strong> entries
							</div>
							<div className="dataTables_paginate paging_simple_numbers d-flex align-items-center">
								<div className="me-3">
									<select
										className="form-select form-select-sm form-control"
										value={currentPageSize}
										onChange={handlePageSizeChange}
										style={{ width: "auto" }}
									>
										{[10, 25, 50, 100].map((size) => (
											<option key={size} value={size}>
												{size} per page
											</option>
										))}
									</select>
								</div>
								<div className="pagination-controls d-flex">
									<button
										className="paginate_button previous btn btn-sm btn-primary me-2"
										onClick={() => setCurrentPage(1)}
										disabled={currentPage === 1}
									>
										<i className="fa fa-angle-double-left"></i>
									</button>
									<button
										className="paginate_button previous btn btn-sm btn-primary me-2"
										onClick={() => setCurrentPage(currentPage - 1)}
										disabled={currentPage === 1}
									>
										<i className="fa fa-angle-left"></i>
									</button>
									<span className="mx-3 d-flex align-items-center">
										Page{" "}
										<strong className="mx-2">
											{currentPage} of {pagination?.totalPages || 1}
										</strong>
									</span>
									<button
										className="paginate_button next btn btn-sm btn-primary ms-2"
										onClick={() => setCurrentPage(currentPage + 1)}
										disabled={currentPage === (pagination?.totalPages || 1)}
									>
										<i className="fa fa-angle-right"></i>
									</button>
									<button
										className="paginate_button next btn btn-sm btn-primary ms-2"
										onClick={() => setCurrentPage(pagination?.totalPages || 1)}
										disabled={currentPage === (pagination?.totalPages || 1)}
									>
										<i className="fa fa-angle-double-right"></i>
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
export default ManageDepositors;
